import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const REFRESH_COOLDOWN_KEY = "chunk_error_refresh";
const REFRESH_COOLDOWN_MS = 10_000;

/**
 * Detects chunk/dynamic-import loading errors:
 * - "Failed to fetch dynamically imported module" (Vite)
 * - "Loading chunk ... failed" (Webpack)
 * - "ChunkLoadError" (Webpack)
 * - Server returning HTML instead of JS (CDN/routing misconfiguration)
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("ChunkLoadError") ||
    msg.includes("text/html")
  );
}

/**
 * Reload the page once to pick up fresh chunk URLs from a new deployment.
 * Bails out if we already reloaded within the cooldown window (prevents loops).
 * Returns true if a reload was triggered, false if skipped.
 */
export function reloadOnceForChunkError(): boolean {
  const last = sessionStorage.getItem(REFRESH_COOLDOWN_KEY);
  const now = Date.now();
  if (last && now - parseInt(last, 10) < REFRESH_COOLDOWN_MS) return false;

  sessionStorage.setItem(REFRESH_COOLDOWN_KEY, String(now));
  window.location.reload();
  return true;
}

/**
 * Drop-in replacement for React.lazy that retries failed dynamic imports.
 *
 * React.lazy permanently caches a rejected promise — if a chunk fetch fails
 * (network blip, HMR disconnect, stale deployment), the component is broken
 * for the rest of the session. This wrapper retries with exponential backoff
 * before letting the error propagate.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  maxRetries = 1,
): LazyExoticComponent<T> {
  return lazy(() => retryImport(factory, maxRetries));
}

async function retryImport<T>(
  factory: () => Promise<T>,
  retriesLeft: number,
): Promise<T> {
  try {
    return await factory();
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    if (retriesLeft <= 0) {
      // The chunk is gone, not slow — this tab belongs to a superseded
      // deployment. Reload HERE, before the error can reach the boundary:
      // the never-resolving promise keeps the route's Suspense ground up
      // for the reload beat instead of flashing "Something went wrong".
      if (reloadOnceForChunkError()) {
        return new Promise<never>(() => {});
      }
      // Already reloaded seconds ago and still failing — genuinely broken
      // (offline, retention exceeded). Let the boundary say so.
      throw error;
    }

    // One quick retry for transient blips; a 404'd chunk fails fast anyway.
    await new Promise(resolve => setTimeout(resolve, 300));

    return retryImport(factory, retriesLeft - 1);
  }
}
